import { Dispatch, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';

import Container from '@ui/container/container';
import Modal from '@ui/modal/modal';
import Text from '@ui/text/text/text';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsDeletingSession, getWorkingProject } from '@/store/slices/project/selectors';
import { deleteSession } from '@/store/slices/project';
import { getDefaultLanguage } from '@/utils/get-default-language';
import Button from '@ui/buttons/button/button';
import getThemeColorCSSVariable from '@/utils/colors/get-theme-color-css-variable';
import getThemeColorContrastCSSVariable from '@/utils/colors/get-theme-color-contrast-css-variable';
import { EColorVariablesV2 } from '@/types/theme-packs.types';

type DeleteSessionModalProps = {
  projectUuid?: string;
  sessionId?: number;
  showDeleteModal: boolean;
  setShowDeleteModal: Dispatch<SetStateAction<boolean>>;
};

const DeleteSessionModal = ({
  projectUuid,
  sessionId,
  showDeleteModal,
  setShowDeleteModal,
}: DeleteSessionModalProps) => {
  const project = useAppSelector(getWorkingProject);
  const deleting = useAppSelector(selectIsDeletingSession);

  const session = project?.sessions.find((s) => s.session === sessionId);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleClose = () => {
    if (deleting) return null;
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (deleting) return null;
    if (!sessionId || !projectUuid || !project) return null;
    if (projectUuid !== project.uuid) return null;
    if (project.sessions.length === 1) return null;

    await dispatch(deleteSession({ projectUuid, sessionId }));
    setShowDeleteModal(false);
    const defaultLanguage = getDefaultLanguage(project);
    navigate(`/project/edit/${projectUuid}/${defaultLanguage}`, { replace: true });
  };

  if (!projectUuid || !session) return null;
  return (
    <Modal
      open={showDeleteModal}
      customSize={{
        width: '400px',
        height: 'auto',
        maxHeight: '300px',
        minHeight: '200px',
        maxWidth: 'calc(100vw - 80px)',
      }}
      onRequestClose={handleClose}
      RenderHeaderContent={() => {
        return (
          <Container background="var(--gray-900)" className="padding-16" borderRadius="12px 12px 0 0">
            <Text clamp={2} whiteSpace="wrap" tag="h3" color="var(--gray-100)" className="H3 margin-b-8">
              Delete {session.title.base}
            </Text>
          </Container>
        );
      }}
      RenderFooterContent={() => {
        return (
          <Container
            background="var(--gray-900)"
            borderRadius="0 0 12px 12px"
            className="flex fd-row jc-flex-end ai-center padding-16"
          >
            <Button
              design="LocalBttnSec"
              className="L1 padding-v-8 padding-h-16 margin-r-8"
              onClick={handleClose}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              className="flex ai-center jc-center padding-v-8 padding-h-16"
              loading={deleting}
              onClick={handleDelete}
              disabled={deleting}
              backgroundColor={getThemeColorCSSVariable()}
            >
              <Text
                color={getThemeColorContrastCSSVariable(EColorVariablesV2.buttonText, EColorVariablesV2.primary)}
                className="L1"
              >
                Delete
              </Text>
            </Button>
          </Container>
        );
      }}
    >
      <Container background="var(--gray-900)" className="padding-16 overflow-auto" height="100%">
        <Text tag="p" color="var(--gray-300)" className="P2">
          Are you sure you want to delete this session?
        </Text>
      </Container>
    </Modal>
  );
};

export default DeleteSessionModal;
